import type { RefObject } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useTheme } from '../../contexts/ThemeContext';
import { HelpMenu } from '../feedback/HelpMenu';
import { managedAccessText, managingIndicatorText } from '../../contexts/managedAccess';

export interface HeaderNav {
  /** id of the sidebar the toggle controls (aria-controls). */
  id: string;
  open: boolean;
  toggleRef: RefObject<HTMLButtonElement>;
  onToggle: () => void;
}

export function Header({ nav }: { nav?: HeaderNav } = {}) {
  const { user, signOut } = useAuth();
  const { tenantId, tenant, tenants, setTenantId } = useTenant();
  // A superadmin can be inside a tenant they are not a member of (entered
  // from Platform admin): no switcher option exists for it, so show its name.
  const activeIsMembership = tenants.some((t) => t.tenantId === tenantId);
  const { theme, toggleTheme } = useTheme();
  // Acting through a management grant: say so, persistently, with the provider that holds it.
  const managed = tenant?.delegated ? tenant : null;
  const providerName = managed?.delegated
    ? tenants.find((t) => t.tenantId === managed.delegated?.viaTenantId && !t.delegated)?.name
    : undefined;

  return (
    <header className="app-header">
      <div className="app-header-left">
        {nav && (
          <button
            type="button"
            ref={nav.toggleRef}
            className="btn btn-ghost nav-toggle"
            aria-label="Menu"
            aria-expanded={nav.open}
            aria-controls={nav.id}
            onClick={nav.onToggle}
          >
            ☰
          </button>
        )}
        {tenants.length > 1 && activeIsMembership ? (
          <>
            <label htmlFor="tenant-switcher" className="visually-hidden">
              Active tenant
            </label>
            <select
              id="tenant-switcher"
              name="tenant-switcher"
              className="tenant-switcher"
              value={tenantId ?? ''}
              onChange={(e) => setTenantId(e.target.value)}
            >
              {tenants.map((t) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.delegated ? `${t.name} (managed) · ${managedAccessText(t)}` : t.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <span className="tenant-name">{tenant?.name ?? ''}</span>
        )}
        {managed && (
          <span className="managed-indicator" role="status">
            {managingIndicatorText(managed, providerName)}
          </span>
        )}
      </div>
      <div className="app-header-right">
        <HelpMenu />
        <button
          type="button"
          className="btn btn-ghost"
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
        <span className="header-user" title={user?.email ?? ''}>
          {user?.displayName || user?.email}
        </span>
        <button type="button" className="btn btn-ghost" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </header>
  );
}
